import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CongressionalTransaction } from '../api/models/CongressionalTransaction';
import { Politician } from '../api/models/Politician';
import { congressionalApi } from '../services/congressionalApiService';
import { 
    ArrowTrendingUpIcon, 
    ArrowTrendingDownIcon, 
    ArrowsRightLeftIcon,
    CalendarIcon,
    CurrencyDollarIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

interface CongressionalTransactionComponentProps {
    onTransactionSelect?: (transaction: CongressionalTransaction) => void;
    onPoliticianSelect?: (politician: Politician) => void;
}

type TransactionTypeFilter = 'all' | 'buy' | 'sell' | 'sale';
type DateRange = '7d' | '30d' | '90d' | '1y' | 'all';
type SortOption = 'date' | 'amount' | 'politician';

const CongressionalTransactionComponent: React.FC<CongressionalTransactionComponentProps> = ({
    onTransactionSelect,
    onPoliticianSelect
}) => {
    // State for filters and data
    const [transactions, setTransactions] = useState<CongressionalTransaction[]>([]);
    const [summaryData, setSummaryData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState<TransactionTypeFilter>('all');
    const [dateRange, setDateRange] = useState<DateRange>('all');
    const [sortOption, setSortOption] = useState<SortOption>('date');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalTransactions, setTotalTransactions] = useState(0);
    const itemsPerPage = 25;

    // Calculate date range for API
    const getDateRange = useCallback((range: DateRange) => {
        if (range === 'all') return null;
        
        const now = new Date();
        const startDate = new Date();
        
        switch (range) {
            case '7d':
                startDate.setDate(now.getDate() - 7);
                break;
            case '30d':
                startDate.setDate(now.getDate() - 30);
                break;
            case '90d':
                startDate.setDate(now.getDate() - 90);
                break;
            case '1y':
                startDate.setFullYear(now.getFullYear() - 1);
                break;
        }
        
        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: now.toISOString().split('T')[0]
        };
    }, []);

    // Fetch summary data from API with current filters
    const fetchSummaryData = useCallback(async () => {
        try {
            setSummaryLoading(true);
            
            const dateRangeParams = getDateRange(dateRange);
            const params: any = {};
            
            // Add search filters
            if (searchTerm.trim()) {
                params.assetSymbol = searchTerm.trim();
            }
            
            // Add transaction type filter
            if (typeFilter !== 'all') {
                params.transactionType = typeFilter;
            }
            
            // Add date range filter (use startDate/endDate instead of year for more precise filtering)
            if (dateRangeParams) {
                params.startDate = dateRangeParams.startDate;
                params.endDate = dateRangeParams.endDate;
            }
            
            const summaryResponse = await congressionalApi.getCongressionalSummary(params);
            setSummaryData(summaryResponse);
        } catch (err) {
            console.error('Failed to fetch summary data:', err);
            // Don't set error for summary, just log it
        } finally {
            setSummaryLoading(false);
        }
    }, [searchTerm, typeFilter, dateRange, getDateRange]);

    // Fetch transactions from API with current filters
    const fetchTransactions = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const dateRangeParams = getDateRange(dateRange);
            const offset = (currentPage - 1) * itemsPerPage;
            
            const params: any = {
                limit: itemsPerPage,
                offset,
                sort: sortOption
            };
            
            // Add search filters
            if (searchTerm.trim()) {
                params.assetSymbol = searchTerm.trim();
            }
            
            // Add transaction type filter
            if (typeFilter !== 'all') {
                params.transactionType = typeFilter;
            }
            
            // Add date range filter
            if (dateRangeParams) {
                params.startDate = dateRangeParams.startDate;
                params.endDate = dateRangeParams.endDate;
            }
            
            const response = await congressionalApi.getAllTransactions(params);
            
            setTransactions(response.transactions || []);
            setTotalTransactions(response.total || 0);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch transactions');
        } finally {
            setLoading(false);
        }
    }, [searchTerm, typeFilter, dateRange, sortOption, currentPage, getDateRange]);

    // Fetch both transactions and summary data when filters change
    useEffect(() => {
        fetchTransactions();
        fetchSummaryData();
    }, [fetchTransactions, fetchSummaryData]);

    // Reset to first page when filters change (but not when page changes)
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, typeFilter, dateRange, sortOption]);

    const totalPages = Math.ceil(totalTransactions / itemsPerPage);

    const getTransactionIcon = (type: string) => {
        const lowerType = type.toLowerCase();
        if (lowerType.includes('buy') || lowerType.includes('purchase')) {
            return <ArrowTrendingUpIcon className="w-4 h-4 text-green-500" />;
        } else if (lowerType.includes('sell') || lowerType.includes('sale')) {
            return <ArrowTrendingDownIcon className="w-4 h-4 text-red-500" />;
        } else {
            return <ArrowsRightLeftIcon className="w-4 h-4 text-blue-500" />;
        }
    };

    const getTransactionColor = (type: string) => {
        const lowerType = type.toLowerCase();
        if (lowerType.includes('buy') || lowerType.includes('purchase')) {
            return 'text-green-600 bg-green-100';
        } else if (lowerType.includes('sell') || lowerType.includes('sale')) {
            return 'text-red-600 bg-red-100';
        } else {
            return 'text-blue-600 bg-blue-100';
        }
    };

    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return 'Unknown';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleSort = (option: SortOption) => {
        setSortOption(option);
    };

    // Get summary statistics from API data
    const summaryStats = useMemo(() => {
        if (!summaryData) {
            return {
                totalTransactions: totalTransactions,
                uniquePoliticians: 0,
                buyTransactions: 0,
                sellTransactions: 0
            };
        }

        return {
            totalTransactions: summaryData.totalTrades || totalTransactions,
            uniquePoliticians: summaryData.totalPoliticians || 0,
            buyTransactions: summaryData.byTransactionType?.buy || 0,
            sellTransactions: summaryData.byTransactionType?.sell || 0
        };
    }, [summaryData, totalTransactions]);

    return (
        <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
            <div className="mb-6">
                <h2 className="text-2xl font-semibold text-primary-900 mb-4">
                    Congressional Financial Transactions ({totalTransactions.toLocaleString()})
                </h2>

                {/* Filters and Search */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    {/* Search */}
                    <div className="relative">
                        <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" />
                        <input
                            type="text"
                            placeholder="Search assets..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-3 py-2 w-full border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                    </div>

                    {/* Transaction Type Filter */}
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value as TransactionTypeFilter)}
                        className="px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                        <option value="all">All Transactions</option>
                        <option value="buy">Purchases</option>
                        <option value="sale">Sales</option>
                    </select>

                    {/* Date Range Filter */}
                    <select
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value as DateRange)}
                        className="px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                        <option value="7d">Last 7 Days</option>
                        <option value="30d">Last 30 Days</option>
                        <option value="90d">Last 90 Days</option>
                        <option value="1y">Last Year</option>
                        <option value="all">All Time</option>
                    </select>

                    {/* Sort Options */}
                    <select
                        value={sortOption}
                        onChange={(e) => handleSort(e.target.value as SortOption)}
                        className="px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                        <option value="date">Sort by Date (Newest First)</option>
                        <option value="amount">Sort by Amount</option>
                        <option value="politician">Sort by Politician</option>
                    </select>
                </div>

                {/* Summary Statistics */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-neutral-50 p-3 rounded-lg text-center">
                        {summaryLoading ? (
                            <div className="animate-pulse">
                                <div className="h-8 bg-neutral-300 rounded mb-1"></div>
                                <div className="h-3 bg-neutral-300 rounded"></div>
                            </div>
                        ) : (
                            <>
                                <p className="text-2xl font-bold text-primary-600">{summaryStats.totalTransactions.toLocaleString()}</p>
                                <p className="text-xs text-neutral-600">Total Transactions</p>
                            </>
                        )}
                    </div>
                    <div className="bg-neutral-50 p-3 rounded-lg text-center">
                        {summaryLoading ? (
                            <div className="animate-pulse">
                                <div className="h-8 bg-neutral-300 rounded mb-1"></div>
                                <div className="h-3 bg-neutral-300 rounded"></div>
                            </div>
                        ) : (
                            <>
                                <p className="text-2xl font-bold text-green-600">{summaryStats.buyTransactions.toLocaleString()}</p>
                                <p className="text-xs text-neutral-600">Purchases</p>
                            </>
                        )}
                    </div>
                    <div className="bg-neutral-50 p-3 rounded-lg text-center">
                        {summaryLoading ? (
                            <div className="animate-pulse">
                                <div className="h-8 bg-neutral-300 rounded mb-1"></div>
                                <div className="h-3 bg-neutral-300 rounded"></div>
                            </div>
                        ) : (
                            <>
                                <p className="text-2xl font-bold text-red-600">{summaryStats.sellTransactions.toLocaleString()}</p>
                                <p className="text-xs text-neutral-600">Sales</p>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    <span className="ml-2 text-neutral-600">Loading transactions...</span>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-red-600">{error}</p>
                    <button 
                        onClick={fetchTransactions}
                        className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Transactions List */}
            {!loading && !error && (
                <div className="space-y-3">
                    {transactions.map((transaction: CongressionalTransaction) => (
                        <div
                            key={transaction.id}
                            className="border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => onTransactionSelect?.(transaction)}
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex items-start space-x-3 flex-grow">
                                    <div className="flex-shrink-0 mt-1">
                                        {getTransactionIcon(transaction.transactionType || '')}
                                    </div>
                                    <div className="flex-grow min-w-0">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <h3 className="font-medium text-neutral-900 truncate">
                                                {transaction.asset}
                                            </h3>
                                            <span 
                                                className={`px-2 py-1 rounded-full text-xs font-medium ${getTransactionColor(transaction.transactionType || '')}`}
                                            >
                                                {transaction.transactionType || 'Unknown'}
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-4 text-sm text-neutral-600">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    transaction.politician && onPoliticianSelect?.(transaction.politician);
                                                }}
                                                className="hover:text-primary-600 font-medium"
                                            >
                                                {transaction.politician?.name || 'Unknown'}
                                            </button>
                                            <div className="flex items-center space-x-1">
                                                <CalendarIcon className="w-3 h-3" />
                                                <span>{formatDate(transaction.date)}</span>
                                            </div>
                                            <div className="flex items-center space-x-1">
                                                <CurrencyDollarIcon className="w-3 h-3" />
                                                <span>{transaction.amount}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {!loading && !error && totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-6">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded border border-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50"
                    >
                        Previous
                    </button>
                    
                    <span className="text-sm text-neutral-600">
                        Page {currentPage} of {totalPages}
                    </span>
                    
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded border border-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-50"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Empty State */}
            {!loading && !error && transactions.length === 0 && (
                <div className="text-center py-8 text-neutral-500">
                    No transactions found matching your criteria.
                </div>
            )}
        </div>
    );
};

export default CongressionalTransactionComponent;
